import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useRegistrationStore } from '@/lib/store/registrationStore';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';

interface StepAboutYouProps {
  onNext: () => void;
}

export function StepAboutYou({ onNext }: StepAboutYouProps) {
  const { formData, updateFormData, error, intent } = useRegistrationStore();
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const t = usePageTranslation({
    en: authTranslations,
    th: thAuthTranslations,
  });

  // Intent-personalized headlines (falls back to generic)
  const intentHeadlines: Record<string, { headline: string; subline: string }> = {
    chat: { headline: t.intentAboutYouHeadlineChat, subline: t.intentAboutYouSublineChat },
    selling: { headline: t.intentAboutYouHeadlineSelling, subline: t.intentAboutYouSublineSelling },
    pos: { headline: t.intentAboutYouHeadlinePos, subline: t.intentAboutYouSublinePos },
    crm: { headline: t.intentAboutYouHeadlineCrm, subline: t.intentAboutYouSublineCrm },
  };
  const headline = (intent && intentHeadlines[intent]?.headline) || t.tellMeAboutYourself;

  const validate = () => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = t.errorNameRequired;
    }

    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground md:text-center">
          {headline}
        </h2>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">{t.yourName}</Label>
          <Input
            id="firstName"
            value={formData.firstName}
            onChange={(e) => updateFormData({ firstName: e.target.value })}
            placeholder={t.namePlaceholder}
            className={`h-12 rounded-md !border-border !bg-background ${localErrors.firstName ? 'border-destructive' : ''}`}
            autoFocus
          />
          {localErrors.firstName && (
            <p className="text-sm text-destructive">{localErrors.firstName}</p>
          )}
        </div>

      </div>

      {error && <p className="text-sm text-destructive text-center">{error}</p>}

      <Button onClick={handleNext} className="w-full h-12 rounded-md text-base">
        {t.next}
      </Button>
    </div>
  );
}
